'use client';
'use strict';

var navigation_js = require('next/navigation.js');
var React = require('react');
var bippy = require('bippy');
var signals = require('@preact/signals');
require('preact/hooks');
require('preact');
require('preact/jsx-runtime');

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  }
  n.default = e;
  return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespace(React);

var unstableTypes = ["function", "object"];
var isValidElement = (value) => {
  return typeof value === "object" && value !== null && "$$typeof" in value && typeof value.$$typeof === "symbol" && String(value.$$typeof) === "Symbol(react.element)";
};
var fastSerialize = (value) => {
  switch (typeof value) {
    case "function":
      return value.toString();
    case "string":
      return value;
    case "object":
      if (value === null) {
        return "null";
      }
      if (Array.isArray(value)) {
        return value.length > 0 ? "[\u2026]" : "[]";
      }
      if (isValidElement(value)) {
        return `<${bippy.getDisplayName(value.type) ?? ""}${Object.keys(value.props || {}).length > 0 ? " \u2026" : ""}>`;
      }
      if (typeof value === "object" && value !== null && value.constructor === Object) {
        for (const key in value) {
          if (Object.prototype.hasOwnProperty.call(value, key)) {
            return "{\u2026}";
          }
        }
        return "{}";
      }
      const tagString = Object.prototype.toString.call(value).slice(8, -1);
      if (tagString === "Object") {
        const proto = Object.getPrototypeOf(value);
        const constructor = proto?.constructor;
        if (typeof constructor === "function") {
          return `${constructor.displayName || constructor.name || ""}{\u2026}`;
        }
      }
      return `${tagString}{\u2026}`;
    default:
      return String(value);
  }
};
var getPropsRender = (fiber, type) => {
  const changes = [];
  const prevProps = fiber.alternate?.memoizedProps;
  const nextProps = fiber.memoizedProps;
  for (const propName in { ...prevProps, ...nextProps }) {
    const prevValue = prevProps?.[propName];
    const nextValue = nextProps?.[propName];
    if (Object.is(prevValue, nextValue) || isValidElement(prevValue) || isValidElement(nextValue)) {
      continue;
    }
    const change = {
      name: propName,
      prevValue,
      nextValue,
      unstable: false
    };
    changes.push(change);
    const prevValueString = fastSerialize(prevValue);
    const nextValueString = fastSerialize(nextValue);
    if (!unstableTypes.includes(typeof prevValue) || !unstableTypes.includes(typeof nextValue) || prevValueString !== nextValueString) {
      continue;
    }
    change.unstable = true;
  }
  const { selfTime } = bippy.getTimings(fiber);
  return {
    type: "props",
    count: 1,
    trigger: false,
    changes,
    name: bippy.getDisplayName(type),
    time: selfTime,
    forget: bippy.hasMemoCache(fiber)
  };
};
var getContextRender = (fiber, type) => {
  const changes = [];
  const result = bippy.traverseContexts(fiber, (prevContext, nextContext) => {
    const prevValue = prevContext.memoizedValue;
    const nextValue = nextContext.memoizedValue;
    const change = {
      name: "",
      prevValue,
      nextValue,
      unstable: false
    };
    changes.push(change);
    const prevValueString = fastSerialize(prevValue);
    const nextValueString = fastSerialize(nextValue);
    if (unstableTypes.includes(typeof prevValue) && unstableTypes.includes(typeof nextValue) && prevValueString === nextValueString) {
      change.unstable = true;
    }
  });
  if (!result) return null;
  const { selfTime } = bippy.getTimings(fiber);
  return {
    type: "context",
    count: 1,
    trigger: false,
    changes,
    name: bippy.getDisplayName(type),
    time: selfTime,
    forget: bippy.hasMemoCache(fiber)
  };
};
var currentHandlers = {
  devtool: null,
  monitoring: null
};
var createInstrumentation = (params) => {
  const instrumentation = {
    isPaused: signals.signal(false),
    fiberRoots: /* @__PURE__ */ new Set(),
    onCommitFiberRoot: (_rendererID, _root) => {
    }
  };
  currentHandlers[params.kind] = params;
  const createHandleRender = (handler2) => (fiber) => {
    const type = bippy.getType(fiber.type);
    if (!type) return null;
    if (!handler2.isValidFiber(fiber)) return null;
    const propsRender = getPropsRender(fiber, type);
    const contextRender = getContextRender(fiber, type);
    let trigger = false;
    if (fiber.alternate) {
      const didStateChange = bippy.traverseState(fiber, (prevState, nextState) => {
        return !Object.is(prevState.memoizedState, nextState.memoizedState);
      });
      if (didStateChange) {
        trigger = true;
      }
    }
    const name = bippy.getDisplayName(type);
    if (name === "Million(Profiler)") return;
    if (!propsRender && !contextRender) return null;
    const renders = [];
    if (propsRender) {
      propsRender.trigger = trigger;
      renders.push(propsRender);
    }
    if (contextRender) {
      contextRender.trigger = trigger;
      renders.push(contextRender);
    }
    const { selfTime } = bippy.getTimings(fiber);
    if (trigger) {
      renders.push({
        type: "state",
        count: 1,
        trigger,
        changes: [],
        name: bippy.getDisplayName(type),
        time: selfTime,
        forget: bippy.hasMemoCache(fiber)
      });
    }
    if (!propsRender && !contextRender && !trigger) {
      renders.push({
        type: "misc",
        count: 1,
        trigger,
        changes: [],
        name: bippy.getDisplayName(type),
        time: selfTime,
        forget: bippy.hasMemoCache(fiber)
      });
    }
    handler2.onRender(fiber, renders);
  };
  const handler = currentHandlers[params.kind];
  const visitor = bippy.createFiberVisitor({
    onRender: createHandleRender(handler),
    onError: (error) => {
      console.error("[React Scan] Error instrumenting: ", error);
    }
  });
  const onCommitFiberRoot = (rendererID, root) => {
    if (instrumentation.isPaused.value) return;
    currentHandlers.devtool?.onCommitStart();
    currentHandlers.monitoring?.onCommitStart();
    if (root) {
      instrumentation.fiberRoots.add(root);
    }
    visitor(rendererID, root);
    currentHandlers.devtool?.onCommitFinish();
    currentHandlers.monitoring?.onCommitFinish();
  };
  bippy.instrument({ onCommitFiberRoot });
  instrumentation.onCommitFiberRoot = onCommitFiberRoot;
  return instrumentation;
};

// src/core/utils.ts
var addFiberToSet = (fiber, set) => {
  if (fiber.alternate && set.has(fiber.alternate)) {
    return;
  }
  set.add(fiber);
};
var isValidFiber = (fiber) => {
  if (ignoredProps.has(fiber.memoizedProps)) {
    return false;
  }
  const allowList = ReactScanInternals.componentAllowList;
  const shouldAllow = allowList?.has(fiber.type) ?? allowList?.has(fiber.elementType);
  if (shouldAllow) {
    const parent = bippy.traverseFiber(
      fiber,
      (node) => {
        const options = allowList?.get(node.type) ?? allowList?.get(node.elementType);
        return options?.includeChildren;
      },
      true
    );
    if (!parent && !shouldAllow) return false;
  }
  return true;
};
var updateFiberRenderData = (fiber, renders) => {
  ReactScanInternals.options.value.onRender?.(fiber, renders);
  const type = bippy.getType(fiber.type) || fiber.type;
  if (type && typeof type === "function" && typeof type === "object") {
    const renderData = type.renderData || {
      count: 0,
      time: 0,
      renders: []
    };
    const firstRender = renders[0];
    renderData.count += firstRender.count;
    renderData.time += firstRender.time;
    renderData.renders.push(firstRender);
    type.renderData = renderData;
  }
};

// src/core/web/utils.ts
var onIdle = (callback) => {
  if ("scheduler" in globalThis) {
    return globalThis.scheduler.postTask(callback, {
      priority: "background"
    });
  }
  if ("requestIdleCallback" in window) {
    return requestIdleCallback(callback);
  }
  return setTimeout(callback, 0);
};

// src/core/web/outline.ts
var DEFAULT_THROTTLE_TIME = 32;
var rectCache = /* @__PURE__ */ new Map();
var getRect = (domNode) => {
  const now = performance.now();
  const cached = rectCache.get(domNode);
  if (cached && now - cached.timestamp < DEFAULT_THROTTLE_TIME) {
    return cached.rect;
  }
  const style = window.getComputedStyle(domNode);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
    return null;
  }
  const rect = domNode.getBoundingClientRect();
  const isVisible = rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  if (!isVisible || !rect.width || !rect.height) {
    return null;
  }
  rectCache.set(domNode, { rect, timestamp: now });
  return rect;
};
var getFiberFromElement = (element) => {
  if ("__REACT_DEVTOOLS_GLOBAL_HOOK__" in window) {
    const { renderers } = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (!renderers) return null;
    for (const [_, renderer] of Array.from(renderers)) {
      try {
        const fiber = renderer.findFiberByHostInstance(element);
        if (fiber) return fiber;
      } catch (e) {
      }
    }
  }
  if ("_reactRootContainer" in element) {
    return element._reactRootContainer?._internalRoot?.current?.child;
  }
  for (const key in element) {
    if (key.startsWith("__reactInternalInstance$") || key.startsWith("__reactFiber")) {
      return element[key];
    }
  }
  return null;
};
var getFirstStateNode = (fiber) => {
  let current = fiber;
  while (current) {
    if (current.stateNode instanceof Element) {
      return current.stateNode;
    }
    if (!current.child) {
      break;
    }
    current = current.child;
  }
  while (current) {
    if (current.stateNode instanceof Element) {
      return current.stateNode;
    }
    if (!current.return) {
      break;
    }
    current = current.return;
  }
  return null;
};
var getNearestFiberFromElement = (element) => {
  if (!element) return null;
  const target = element;
  const originalFiber = getFiberFromElement(target);
  if (!originalFiber) {
    return null;
  }
  const res = getParentCompositeFiber(originalFiber);
  if (!res) {
    return null;
  }
  return res[0];
};
var getParentCompositeFiber = (fiber) => {
  let curr = fiber;
  let prevNonHost = null;
  while (curr) {
    if (bippy.isCompositeFiber(curr)) {
      return [curr, prevNonHost];
    }
    if (bippy.isHostFiber(curr)) {
      prevNonHost = curr;
    }
    curr = curr.return;
  }
};
var isFiberInTree = (fiber, root) => {
  return !!bippy.traverseFiber(root, (searchFiber) => searchFiber === fiber);
};
var isCurrentTree = (fiber) => {
  let curr = fiber;
  let rootFiber = null;
  while (curr) {
    if (curr.stateNode && ReactScanInternals.instrumentation?.fiberRoots.has(curr.stateNode)) {
      rootFiber = curr;
      break;
    }
    curr = curr.return;
  }
  if (!rootFiber) {
    return false;
  }
  const fiberRoot = rootFiber.stateNode;
  const currentRootFiber = fiberRoot.current;
  return isFiberInTree(fiber, currentRootFiber);
};
var getCompositeComponentFromElement = (element) => {
  const associatedFiber = getNearestFiberFromElement(element);
  if (!associatedFiber) return {};
  const currentAssociatedFiber = isCurrentTree(associatedFiber) ? associatedFiber : associatedFiber.alternate ?? associatedFiber;
  const stateNode = getFirstStateNode(currentAssociatedFiber);
  if (!stateNode) return {};
  const targetRect = getRect(stateNode);
  if (!targetRect) return {};
  const anotherRes = getParentCompositeFiber(currentAssociatedFiber);
  if (!anotherRes) {
    return {};
  }
  let [parentCompositeFiber] = anotherRes;
  parentCompositeFiber = (isCurrentTree(parentCompositeFiber) ? parentCompositeFiber : parentCompositeFiber.alternate) ?? parentCompositeFiber;
  return {
    parentCompositeFiber,
    targetRect
  };
};
signals.signal(false);
signals.signal(
  parseInt(
    typeof window !== "undefined" ? localStorage.getItem("react-scan-toolbar-x") ?? String(window.innerWidth - 400) : "0"
  )
);
signals.signal(
  parseInt(
    typeof window !== "undefined" ? localStorage.getItem("react-scan-toolbar-y") ?? String(window.innerHeight - 500) : "0"
  )
);
signals.signal(false);
signals.signal(false);

// src/core/index.ts
var Store = {
  isInIframe: signals.signal(
    typeof window !== "undefined" && window.self !== window.top
  ),
  inspectState: signals.signal({
    kind: "uninitialized"
  }),
  monitor: signals.signal(null),
  fiberRoots: /* @__PURE__ */ new WeakSet(),
  reportData: /* @__PURE__ */ new WeakMap(),
  legacyReportData: /* @__PURE__ */ new Map(),
  lastReportTime: signals.signal(0)
};
var ReactScanInternals = {
  instrumentation: null,
  componentAllowList: null,
  options: signals.signal({
    enabled: true,
    includeChildren: true,
    playSound: false,
    log: false,
    showToolbar: true,
    renderCountThreshold: 0,
    report: void 0,
    alwaysShowLabels: false,
    animationSpeed: "fast"
  }),
  onRender: null,
  scheduledOutlines: [],
  activeOutlines: [],
  Store
};
var setOptions = (options) => {
  const { instrumentation } = ReactScanInternals;
  if (instrumentation) {
    instrumentation.isPaused.value = options.enabled === false;
  }
  ReactScanInternals.options.value = {
    ...ReactScanInternals.options.value,
    ...options
  };
};
var reportRender = (fiber, renders) => {
  let reportFiber;
  let prevRenderData;
  const currentFiberData = Store.reportData.get(fiber);
  if (currentFiberData) {
    reportFiber = fiber;
    prevRenderData = currentFiberData;
  } else if (!fiber.alternate) {
    reportFiber = fiber;
    prevRenderData = void 0;
  } else {
    reportFiber = fiber.alternate;
    prevRenderData = Store.reportData.get(fiber.alternate);
  }
  const displayName = bippy.getDisplayName(fiber.type);
  Store.lastReportTime.value = performance.now();
  if (prevRenderData) {
    prevRenderData.renders.push(...renders);
  } else {
    const { selfTime } = bippy.getTimings(fiber);
    const reportData = {
      count: renders.length,
      time: selfTime,
      renders,
      displayName,
      type: null
    };
    Store.reportData.set(reportFiber, reportData);
  }
  if (displayName && ReactScanInternals.options.value.report) {
    const prevLegacyRenderData = Store.legacyReportData.get(displayName);
    if (prevLegacyRenderData) {
      prevLegacyRenderData.renders.push(...renders);
    } else {
      const { selfTime } = bippy.getTimings(fiber);
      const reportData = {
        count: renders.length,
        time: selfTime,
        renders,
        displayName: null,
        type: bippy.getType(fiber.type) || fiber.type
      };
      Store.legacyReportData.set(displayName, reportData);
    }
  }
};
var ignoredProps = /* @__PURE__ */ new WeakSet();
var DEFAULT_FILTERS = {
  skipProviders: true,
  skipHocs: true,
  skipContainers: true,
  skipMinified: true,
  skipUtilities: true,
  skipBoundaries: true
};
var FILTER_PATTERNS = {
  providers: [/Provider$/, /^Provider$/, /^Context$/],
  hocs: [/^with[A-Z]/, /^forward(Ref)?$/i, /^Forward(Ref)?\(/],
  containers: [/^(App)?Container$/, /^Root$/, /^ReactDev/],
  utilities: [
    /^Fragment$/,
    /^Suspense$/,
    /^ErrorBoundary$/,
    /^Portal$/,
    /^Consumer$/,
    /^Layout$/,
    /^Router/,
    /^Hydration/
  ],
  boundaries: [/^Boundary$/, /Boundary$/, /^Provider$/, /Provider$/]
};
function shouldIncludeInPath(name, filters = DEFAULT_FILTERS) {
  const patternsToCheck = [];
  if (filters.skipProviders) patternsToCheck.push(...FILTER_PATTERNS.providers);
  if (filters.skipHocs) patternsToCheck.push(...FILTER_PATTERNS.hocs);
  if (filters.skipContainers)
    patternsToCheck.push(...FILTER_PATTERNS.containers);
  if (filters.skipUtilities) patternsToCheck.push(...FILTER_PATTERNS.utilities);
  if (filters.skipBoundaries)
    patternsToCheck.push(...FILTER_PATTERNS.boundaries);
  return !patternsToCheck.some((pattern) => pattern.test(name));
}
var isMinified = (name) => {
  if (!name || typeof name !== "string") {
    return true;
  }
  const minifiedPatterns = [
    /^[a-z]$/,
    // Single lowercase letter
    /^[a-z][0-9]$/,
    // Lowercase letter followed by number
    /^_+$/,
    // Just underscores
    /^[A-Za-z][_$]$/,
    // Letter followed by underscore or dollar
    /^[a-z]{1,2}$/
    // 1-2 lowercase letters
  ];
  if (minifiedPatterns.some((pattern) => pattern.test(name))) {
    return true;
  }
  const hasNoVowels = !/[aeiou]/i.test(name);
  const hasMostlyNumbers = (name.match(/\d/g)?.length ?? 0) > name.length / 2;
  const isSingleWordLowerCase = /^[a-z]+$/.test(name);
  const hasRandomLookingChars = /[\$_]{2,}/.test(name);
  const suspiciousTraits = [
    hasNoVowels,
    hasMostlyNumbers,
    isSingleWordLowerCase,
    hasRandomLookingChars
  ].filter(Boolean).length;
  return suspiciousTraits >= 2;
};
function getInteractionPath(fiber, filters = DEFAULT_FILTERS) {
  if (!fiber) return "";
  const fullPath = [];
  const currentName = bippy.getDisplayName(fiber.type);
  if (currentName) {
    fullPath.unshift(currentName);
  }
  let current = fiber.return;
  while (current) {
    if (current.type && typeof current.type === "function") {
      const name = getCleanComponentName(current.type);
      if (name && !isMinified(name) && shouldIncludeInPath(name, filters)) {
        fullPath.unshift(name);
      }
    }
    current = current.return;
  }
  const normalized = normalizePath(fullPath);
  return normalized;
}
var currentMouseOver;
function getCleanComponentName(component) {
  const name = bippy.getDisplayName(component);
  if (!name) return "";
  return name.replace(/^(Memo|Forward(Ref)?|With.*?)\((.*?)\)$/, "$3");
}
function normalizePath(path) {
  const cleaned = path.filter(Boolean);
  const deduped = cleaned.filter((name, i) => name !== cleaned[i - 1]);
  return deduped.join(".");
}
var handleMouseover = (event) => {
  if (!(event.target instanceof Element)) {
    return;
  }
  currentMouseOver = event.target;
};
var getFirstNamedAncestorCompositeFiber = (element) => {
  let curr = element;
  let parentCompositeFiber = null;
  while (!parentCompositeFiber && curr.parentElement) {
    curr = curr.parentElement;
    const { parentCompositeFiber: fiber } = getCompositeComponentFromElement(curr);
    if (!fiber) {
      continue;
    }
    if (bippy.getDisplayName(fiber?.type)) {
      parentCompositeFiber = fiber;
    }
  }
  return parentCompositeFiber;
};
function initPerformanceMonitoring(options) {
  const filters = { ...DEFAULT_FILTERS, ...options };
  const monitor = Store.monitor.value;
  if (!monitor) return;
  document.addEventListener("mouseover", handleMouseover);
  const disconnectPerformanceListener = setupPerformanceListener((entry) => {
    const target = entry.target ?? (entry.type === "pointer" ? currentMouseOver : null);
    if (!target) {
      return;
    }
    const parentCompositeFiber = getFirstNamedAncestorCompositeFiber(target);
    if (!parentCompositeFiber) {
      return;
    }
    const displayName = bippy.getDisplayName(parentCompositeFiber.type);
    if (!displayName || isMinified(displayName)) {
      return;
    }
    const path = getInteractionPath(parentCompositeFiber, filters);
    monitor.interactions.push({
      componentName: displayName,
      componentPath: path,
      performanceEntry: entry,
      components: /* @__PURE__ */ new Map(),
      route: Store.monitor.value?.route ?? null,
      url: window.location.toString(),
      uniqueInteractionId: entry.id
    });
  });
  return () => {
    disconnectPerformanceListener();
    document.removeEventListener("mouseover", handleMouseover);
  };
}
var setupPerformanceListener = (onEntry) => {
  const longestInteractionList = [];
  const longestInteractionMap = /* @__PURE__ */ new Map();
  const interactionTargetMap = /* @__PURE__ */ new Map();
  const processInteractionEntry = (entry) => {
    if (!(entry.interactionId || entry.entryType === "first-input")) return;
    if (entry.interactionId && entry.target && !interactionTargetMap.has(entry.interactionId)) {
      interactionTargetMap.set(entry.interactionId, entry.target);
    }
    const existingInteraction = longestInteractionMap.get(entry.interactionId);
    if (existingInteraction) {
      if (entry.duration > existingInteraction.latency) {
        existingInteraction.entries = [entry];
        existingInteraction.latency = entry.duration;
      } else if (entry.duration === existingInteraction.latency && entry.startTime === existingInteraction.entries[0].startTime) {
        existingInteraction.entries.push(entry);
      }
    } else {
      const interactionType = getInteractionType(entry.name);
      if (!interactionType) {
        return;
      }
      const interaction = {
        id: entry.interactionId,
        latency: entry.duration,
        entries: [entry],
        target: entry.target,
        type: interactionType,
        startTime: entry.startTime,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        duration: entry.duration,
        inputDelay: entry.processingStart - entry.startTime,
        processingDuration: entry.processingEnd - entry.processingStart,
        presentationDelay: entry.duration - (entry.processingEnd - entry.startTime),
        timestamp: Date.now()
      };
      longestInteractionMap.set(interaction.id, interaction);
      longestInteractionList.push(interaction);
      onEntry(interaction);
    }
    longestInteractionList.sort((a, b) => b.latency - a.latency);
  };
  const getInteractionType = (eventName) => {
    if (["pointerdown", "pointerup", "click"].includes(eventName)) {
      return "pointer";
    }
    if (["keydown", "keyup"].includes(eventName)) {
      return "keyboard";
    }
    return null;
  };
  const po = new PerformanceObserver((list) => {
    list.getEntries().forEach(
      (entry) => processInteractionEntry(entry)
    );
  });
  try {
    po.observe({
      type: "event",
      buffered: true,
      durationThreshold: 16
    });
    po.observe({
      type: "first-input",
      buffered: true
    });
  } catch {
  }
  return () => {
    po.disconnect();
  };
};
var isRSC = !React__namespace.useRef;
var isSSR = typeof window === "undefined" || isRSC;
var GZIP_MIN_LEN = 1e3;
var GZIP_MAX_LEN = 6e4;
var MAX_PENDING_REQUESTS = 15;
var FLOAT_MAX_LEN = 1e3;

// src/core/monitor/utils.ts
var getDeviceType = () => {
  const userAgent = navigator.userAgent;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  )) {
    return 2 /* MOBILE */;
  } else if (/iPad|Tablet/i.test(userAgent)) {
    return 1 /* TABLET */;
  }
  return 0 /* DESKTOP */;
};
var generateId = () => {
  const alphabet = "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict";
  let id = "";
  const randomValues = crypto.getRandomValues(new Uint8Array(21));
  for (let i = 0; i < 21; i++) {
    id += alphabet[63 & randomValues[i]];
  }
  return id;
};
var getGpuRenderer = () => {
  if (!("chrome" in window)) return "";
  const gl = document.createElement("canvas").getContext("webgl", { powerPreference: "high-performance" });
  if (!gl) return "";
  const ext = gl.getExtension("WEBGL_debug_renderer_info");
  return ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : "";
};
var cachedSession;
var getSession = async () => {
  if (isSSR) return null;
  if (cachedSession) {
    return cachedSession;
  }
  const id = generateId();
  const url = window.location.toString();
  const connection = navigator.connection;
  const wifi = connection && connection.effectiveType || null;
  const cpu = navigator.hardwareConcurrency;
  const mem = navigator.deviceMemory;
  const gpuRendererPromise = new Promise((resolve) => {
    onIdle(() => {
      resolve(getGpuRenderer());
    });
  });
  const session = {
    id,
    url,
    device: getDeviceType(),
    wifi,
    cpu,
    mem,
    gpu: await gpuRendererPromise,
    agent: navigator.userAgent
  };
  cachedSession = session;
  return session;
};

// src/core/monitor/network.ts
var getInteractionId = (interaction) => `${interaction.performanceEntry.type}::${interaction.componentPath}::${interaction.url}`;
var INTERACTION_TIME_TILL_COMPLETED = 4e3;
var splitInteractions = (interactions) => {
  const now = performance.now();
  const pendingInteractions = [];
  const completedInteractions = [];
  interactions.forEach((interaction) => {
    if (now - interaction.performanceEntry.startTime <= INTERACTION_TIME_TILL_COMPLETED) {
      pendingInteractions.push(interaction);
    } else {
      completedInteractions.push(interaction);
    }
  });
  return { pendingInteractions, completedInteractions };
};
var aggregateComponents = (interactions) => {
  const aggregatedComponents = [];
  for (const interaction of interactions) {
    for (const [name, component] of Array.from(
      interaction.components.entries()
    )) {
      aggregatedComponents.push({
        name,
        instances: component.fibers.size,
        interactionId: getInteractionId(interaction),
        renders: component.renders,
        totalTime: component.totalTime
      });
      if (component.retiresAllowed === 0) {
        interaction.components.delete(name);
      }
      component.retiresAllowed -= 1;
    }
  }
  return aggregatedComponents;
};
var toPayloadInteraction = (interactions) => interactions.map(
  (interaction) => ({
    id: getInteractionId(interaction),
    name: interaction.componentName,
    time: interaction.performanceEntry.duration,
    timestamp: interaction.performanceEntry.timestamp,
    type: interaction.performanceEntry.type,
    route: interaction.route,
    url: interaction.url,
    uniqueInteractionId: interaction.uniqueInteractionId
  })
);
var flush = async () => {
  const monitor = Store.monitor.value;
  if (!monitor || !navigator.onLine || !monitor.url || !monitor.interactions.length) {
    return;
  }
  const { completedInteractions, pendingInteractions } = splitInteractions(
    monitor.interactions
  );
  if (!completedInteractions.length) {
    return;
  }
  const session = await getSession().catch(() => null);
  if (!session) return;
  const aggregatedComponents = aggregateComponents(monitor.interactions);
  const payload = {
    interactions: toPayloadInteraction(completedInteractions),
    components: aggregatedComponents,
    session: {
      ...session
    }
  };
  monitor.pendingRequests++;
  monitor.interactions = monitor.interactions.filter(
    (interaction) => completedInteractions.some(
      (completedInteraction) => completedInteraction.performanceEntry.id !== interaction.performanceEntry.id
    )
  );
  try {
    transport(monitor.url, payload).then(() => {
      monitor.pendingRequests--;
    }).catch(async () => {
      monitor.interactions = monitor.interactions.concat(
        completedInteractions
      );
    });
  } catch {
  }
  monitor.interactions = pendingInteractions;
};
var CONTENT_TYPE = "application/json";
var supportsCompression = typeof CompressionStream === "function";
var compress = async (payload) => {
  const stream = new Blob([payload], { type: CONTENT_TYPE }).stream().pipeThrough(new CompressionStream("gzip"));
  return new Response(stream).arrayBuffer();
};
var transport = async (url, payload) => {
  const fail = { ok: false };
  const json = JSON.stringify(payload, (key, value) => {
    if (typeof value === "number" && parseInt(value) !== value) {
      value = ~~(value * FLOAT_MAX_LEN) / FLOAT_MAX_LEN;
    }
    if (
      // eslint-disable-next-line eqeqeq
      value != null && value !== false || Array.isArray(value) && value.length
    ) {
      return value;
    }
  });
  const shouldCompress = json.length > GZIP_MIN_LEN;
  const body = shouldCompress && supportsCompression ? await compress(json) : json;
  if (!navigator.onLine) return fail;
  const headers = {
    "Content-Type": CONTENT_TYPE,
    "Content-Encoding": shouldCompress ? "gzip" : void 0,
    "x-api-key": Store.monitor.value?.apiKey
  };
  if (shouldCompress) url += "?z=1";
  const size = typeof body === "string" ? body.length : body.byteLength;
  return fetch(url, {
    body,
    method: "POST",
    referrerPolicy: "origin",
    /**
     * Outgoing requests are usually cancelled when navigating to a different page, causing a "TypeError: Failed to
     * fetch" error and sending a "network_error" client-outcome - in Chrome, the request status shows "(cancelled)".
     * The `keepalive` flag keeps outgoing requests alive, even when switching pages. We want this since we're
     * frequently sending events right before the user is switching pages (e.g., when finishing navigation transactions).
     *
     * This is the modern alternative to the navigator.sendBeacon API.
     * @see https://javascript.info/fetch-api#keepalive
     *
     * Gotchas:
     * - `keepalive` isn't supported by Firefox
     * - As per spec (https://fetch.spec.whatwg.org/#http-network-or-cache-fetch):
     *   If the sum of contentLength and inflightKeepaliveBytes is greater than 64 kibibytes, then return a network error.
     *   We will therefore only activate the flag when we're below that limit.
     * - There is also a limit of requests that can be open at the same time, so we also limit this to 15.
     *
     * @see https://github.com/getsentry/sentry-javascript/pull/7553
     */
    keepalive: GZIP_MAX_LEN > size && MAX_PENDING_REQUESTS > (Store.monitor.value?.pendingRequests ?? 0),
    priority: "low",
    // mode: 'no-cors',
    headers
  });
};

// src/core/monitor/index.ts
var MAX_RETRIES_BEFORE_COMPONENT_GC = 7;
var Monitoring = ({
  url,
  apiKey,
  path,
  route
}) => {
  if (!apiKey)
    throw new Error("Please provide a valid API key for React Scan monitoring");
  url ??= "https://monitoring.react-scan.com/api/v1/ingest";
  Store.monitor.value ??= {
    pendingRequests: 0,
    url,
    apiKey,
    interactions: [],
    session: getSession().catch(() => null),
    route,
    path
  };
  Store.monitor.value.route = route;
  Store.monitor.value.path = path;
  React__namespace.default.useEffect(() => {
    scanMonitoring({
      enabled: true
    });
    return initPerformanceMonitoring();
  }, []);
  return null;
};
var scanMonitoring = (options) => {
  setOptions(options);
  startMonitoring();
};
var flushInterval;
var startMonitoring = () => {
  if (!Store.monitor.value) ;
  if (flushInterval) {
    clearInterval(flushInterval);
  }
  flushInterval = setInterval(() => {
    try {
      void flush();
    } catch {
    }
  }, 2e3);
  globalThis.__REACT_SCAN__ = {
    ReactScanInternals
  };
  const instrumentation = createInstrumentation({
    kind: "monitoring",
    onCommitStart() {
      ReactScanInternals.options.value.onCommitStart?.();
    },
    isValidFiber(fiber) {
      return isValidFiber(fiber);
    },
    onRender(fiber, renders) {
      if (ReactScanInternals.instrumentation?.isPaused.value) {
        return;
      }
      updateFiberRenderData(fiber, renders);
      if (bippy.isCompositeFiber(fiber)) {
        reportRender(fiber, renders);
        aggregateComponentRenderToInteraction(fiber, renders);
      }
      ReactScanInternals.options.value.onRender?.(fiber, renders);
    },
    onCommitFinish() {
      ReactScanInternals.options.value.onCommitFinish?.();
    }
  });
  ReactScanInternals.instrumentation = instrumentation;
};
var aggregateComponentRenderToInteraction = (fiber, renders) => {
  const monitor = Store.monitor.value;
  if (monitor && monitor.interactions && monitor.interactions.length > 0) {
    const latestInteraction = monitor.interactions[monitor.interactions.length - 1];
    let totalTime = 0;
    for (const render2 of renders) {
      totalTime += render2.time;
    }
    const displayName = bippy.getDisplayName(fiber.type);
    if (!displayName) {
      return;
    }
    let component = latestInteraction.components.get(displayName);
    if (!component) {
      component = {
        fibers: /* @__PURE__ */ new Set(),
        name: displayName,
        renders: 0,
        totalTime,
        retiresAllowed: MAX_RETRIES_BEFORE_COMPONENT_GC,
        uniqueInteractionId: latestInteraction.uniqueInteractionId
      };
      latestInteraction.components.set(displayName, component);
    }
    addFiberToSet(fiber, component.fibers);
    component.renders += renders.length;
    if (!component.totalTime) {
      component.totalTime = 0;
    }
    component.totalTime += component.totalTime ? component.totalTime + totalTime : totalTime;
  }
};

// src/core/monitor/params/utils.ts
function computeRouteWithFormatter(pathname, pathParams, formatter) {
  if (!pathname || !pathParams) {
    return pathname;
  }
  let result = pathname;
  try {
    const entries = Object.entries(pathParams);
    for (const [key, value] of entries) {
      if (!Array.isArray(value)) {
        const matcher = turnValueToRegExp(value);
        if (matcher.test(result)) {
          result = result.replace(matcher, formatter.param(key));
        }
      }
    }
    for (const [key, value] of entries) {
      if (Array.isArray(value)) {
        const matcher = turnValueToRegExp(value.join("/"));
        if (matcher.test(result)) {
          result = result.replace(matcher, formatter.catchAll(key));
        }
      }
    }
    return result;
  } catch (e) {
    return pathname;
  }
}
function computeRoute(pathname, pathParams) {
  return computeRouteWithFormatter(pathname, pathParams, {
    param: (key) => `/[${key}]`,
    catchAll: (key) => `/[...${key}]`
  });
}
function turnValueToRegExp(value) {
  return new RegExp(`/${escapeRegExp(value)}(?=[/?#]|$)`);
}
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// src/core/monitor/params/next.ts
var useRoute = () => {
  const params = navigation_js.useParams();
  const searchParams = navigation_js.useSearchParams();
  const path = navigation_js.usePathname();
  if (!params) {
    return { route: null, path };
  }
  const finalParams = Object.keys(params).length ? params : Object.fromEntries(searchParams.entries());
  return { route: computeRoute(path, finalParams), path };
};
function MonitoringInner(props) {
  const { route, path } = useRoute();
  return React.createElement(Monitoring, {
    ...props,
    route,
    path
  });
}
function Monitoring2(props) {
  return React.createElement(
    React.Suspense,
    { fallback: null },
    React.createElement(MonitoringInner, props)
  );
}

exports.Monitoring = Monitoring2;
exports.MonitoringInner = MonitoringInner;
